import { useState, useCallback, useRef } from 'react';
import { storage } from './storage';

export const useLocalStorage = <S>(key: string, initialValue: S) => {
  const useKey = `useLocalStorage_${key}`;
  // This reference is to prevent `storedValue` as a `setValue` dependency
  // in the `useCallback` below. This could cause an infinite loop when values are objects.
  const valueRef = useRef<S>();
  const serializedValueRef = useRef<string>();

  const [ storedValue, setStoredValue ] = useState(() => {
    try {
      // Get from local storage by useKey
      const item = storage.getItem(useKey);
      // Parse stored json or if none return initialValue
      const value = item ? JSON.parse(item) : initialValue;
      valueRef.current = value;
      serializedValueRef.current = JSON.stringify(value);

      return value;
    } catch (error) {
      // If error also return initialValue
      return initialValue;
    }
  });

  const setValue = useCallback((value: S) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(valueRef.current) : value;
      const serializedValue = JSON.stringify(valueToStore);
      // Save state
      setStoredValue(valueToStore);
      valueRef.current = valueToStore;
      // We keep a serialized copy in case the value is an object
      serializedValueRef.current = serializedValue;
      // Save to local storage
      storage.setItem(useKey, JSON.stringify(valueToStore));
    } catch (error) {
      // A more advanced implementation would handle the error case
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }, [ useKey ]);

  return [ storedValue, setValue ] as [ S, typeof setValue ];
};
